import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Plus, GripVertical, Eye, EyeOff } from "lucide-react";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { useCategories } from "@/query/useCategories";
import { useMenuItems } from "@/query/useMenuItems";
import { Button } from "@/components/ui/button";
import ButtonWithTooltip from "@/components/ButtonWithTooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Category } from "@/model/entities";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import CategoryDialogForm from "@/components/CategoryDialogForm";
import { toast } from "sonner";
import { usePlan } from "@/hooks/usePlan";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableRowProps {
  category: Category;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onToggleHidden: (category: Category) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  category,
  onEdit,
  onDelete,
  onToggleHidden,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      data-testid="category-item"
      className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
        isDragging ? "z-50" : ""
      }`}
    >
      <TableCell className="w-12">
        <button
          className="cursor-grab active:cursor-grabbing touch-none p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{category.name}</TableCell>
      <TableCell className="text-end">
        <div className="flex justify-start gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleHidden(category)}
            className="h-8 w-8 p-0"
            title={
              category.isHidden
                ? t("manageCategories.unhide")
                : t("manageCategories.hide")
            }
          >
            {category.isHidden ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(category)}
            className="h-8 w-8 p-0"
            title={t("manageCategories.edit")}
            data-testid="edit-category-button"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(category)}
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
            title={t("manageCategories.delete")}
            data-testid="delete-category-button"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const ManageCategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const { restaurant } = useCurrentRestaurant();
  const {
    data: categories,
    isLoading,
    error,
    deleteCategory,
    saveCategories,
    toggleHidden,
  } = useCategories(restaurant?.id || "");
  const { isAllowedToAddCategory } = usePlan();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | undefined>(
    undefined
  );
  const [localCategories, setLocalCategories] = useState<Category[]>([]);

  // Fetch menu items for the category being deleted to count them
  const { data: menuItemsForDeletedCategory } = useMenuItems(
    categoryToDelete?.id
  );

  // Calculate the number of items in the category being deleted
  const itemCountInDeletedCategory = useMemo(() => {
    return menuItemsForDeletedCategory?.length || 0;
  }, [menuItemsForDeletedCategory]);

  // Generate the confirmation dialog description based on item count
  const deleteConfirmDescription = useMemo(() => {
    if (!categoryToDelete) return "";

    if (itemCountInDeletedCategory > 0) {
      return t("category.deleteConfirmWithItems", {
        count: itemCountInDeletedCategory,
        defaultValue: `This category contains {{count}} menu item(s). Deleting this category will also delete all items in it. This action cannot be undone.`,
      });
    }

    return t("category.deleteConfirmDescription", {
      categoryName: categoryToDelete.name,
      defaultValue: `Are you sure you want to delete the category "{{categoryName}}"?`,
    });
  }, [categoryToDelete, itemCountInDeletedCategory, t]);

  // Initialize local categories when data changes
  React.useEffect(() => {
    if (categories) {
      setLocalCategories(categories);
    }
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts (helps with click vs drag)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localCategories.findIndex((cat) => cat.id === active.id);
    const newIndex = localCategories.findIndex((cat) => cat.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newCategories = arrayMove(localCategories, oldIndex, newIndex);
      setLocalCategories(newCategories);

      try {
        await saveCategories.mutateAsync(newCategories);
        toast.success(t("category.orderUpdatedSuccess"));
      } catch (error) {
        console.error("Error updating category order:", error);
        toast.error(t("category.orderUpdateError"));
        // Revert to original order on error
        setLocalCategories(categories || []);
      }
    }
  };

  const handleEdit = (category: Category) => {
    setCategoryToEdit(category);
    setShowAddCategoryDialog(true);
  };

  const handleAddCategory = () => {
    setCategoryToEdit(undefined);
    setShowAddCategoryDialog(true);
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategory.mutateAsync(categoryToDelete.id!);
      toast.success(t("category.deletedSuccess"));
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(t("category.deletedError"));
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const handleToggleHidden = async (category: Category) => {
    try {
      const newHiddenState = !category.isHidden;
      await toggleHidden.mutateAsync({
        categoryId: category.id!,
        isHidden: newHiddenState,
      });
      toast.success(
        newHiddenState
          ? t("category.hiddenSuccess")
          : t("category.unhiddenSuccess")
      );
    } catch (error) {
      console.error("Error toggling category visibility:", error);
      toast.error(t("category.toggleHiddenError"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="mr-2">{t("manageCategories.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">{t("manageCategories.error")}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">
            {t("manageCategories.title")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t("manageCategories.description")}
          </p>
        </div>
        <ButtonWithTooltip
          data-testid="add-category-button"
          onClick={handleAddCategory}
          className="bg-orange-500 hover:bg-orange-600"
          disabled={false}
          disabledTooltip={t("manageCategories.upgradeToAddCategories")}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("manageCategories.addCategory")}
        </ButtonWithTooltip>
      </div>

      {!categories || categories.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium mb-2">
            {t("manageCategories.noCategories")}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t("manageCategories.noCategoriesMessage")}
          </p>
        </div>
      ) : (
        <div className="rounded-md border bg-white text-black dark:bg-gray-900 dark:text-white shadow-sm">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium text-start w-12"></TableHead>
                  <TableHead className="font-medium text-start">
                    {t("manageCategories.tableHeaders.name")}
                  </TableHead>
                  <TableHead className="font-medium text-start w-1/4">
                    {t("manageCategories.tableHeaders.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="categories-list">
                <SortableContext
                  items={localCategories.map((cat) => cat.id!)}
                  strategy={verticalListSortingStrategy}
                >
                  {localCategories.map((category) => (
                    <SortableRow
                      key={category.id}
                      category={category}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleHidden={handleToggleHidden}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        </div>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t("category.deleteConfirmTitle")}
        description={deleteConfirmDescription}
      />

      <CategoryDialogForm
        open={showAddCategoryDialog}
        onClose={() => setShowAddCategoryDialog(false)}
        category={categoryToEdit}
        isAllowedToAddCategory={isAllowedToAddCategory}
      />
    </div>
  );
};

export default ManageCategoriesPage;
